import { useEffect } from 'react';
import { ZERO } from '@drift-labs/sdk';
import { MarketId, MarketKey } from '@drift/common';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';

/**
 * Syncs cross/isolated margin modes from the current account's positions
 * to the market settings store. Event-driven via userAccountUpdate.
 */
export function useMarginModeSync() {
	const currentAccount = useCurrentUserAccount();
	const syncMarginModes = useMarketSettingsStore(
		(s) => s.syncMarketModesFromOpenPositions
	);

	useEffect(() => {
		const user = currentAccount?.client;
		if (!currentAccount?.userKey || !user?.isSubscribed) return;

		const sync = () => {
			const modesMap = new Map<
				MarketKey,
				{ mode: 'cross' | 'isolated'; userKey: string }
			>();

			const ua = user.getUserAccount();
			for (const pos of ua.perpPositions) {
				if (!pos.baseAssetAmount.abs().gt(ZERO)) continue;

				const key = MarketId.createPerpMarket(pos.marketIndex).key;
				const mode: 'cross' | 'isolated' = user.isPerpPositionIsolated(pos)
					? 'isolated'
					: 'cross';
				const existing = modesMap.get(key);
				if (!existing || mode === 'isolated') {
					modesMap.set(key, { mode, userKey: currentAccount.userKey });
				}
			}

			const modes = Array.from(modesMap.entries()).map(
				([marketIdKey, marginMode]) => ({
					marketIdKey,
					marginMode: marginMode.mode,
					userKey: marginMode.userKey,
				})
			);
			syncMarginModes(modes);
		};

		sync();

		user.eventEmitter.on('userAccountUpdate', sync);
		return () => {
			user.eventEmitter.removeListener('userAccountUpdate', sync);
		};
	}, [
		currentAccount?.userKey,
		currentAccount?.client,
		currentAccount?.client?.isSubscribed,
		syncMarginModes,
	]);
}
